import { IRecommendedProductNudgeDataModel } from '@ninjacart/nc-nk-core';
import {
  INinjaEvent,
  addEventParam,
  useQueuedEventStore,
} from '@ninjacart/nc-events-sdk-v2';
import { KissanEventType, useProfileStore } from '@ninjacart/nc-nk-store';
import { Image, Paragraph, Text, View, XStack, YStack } from '@ninjacart/nc-nk-ui';
import { useRouter } from '@ninjacart/nc-nk-web-deps';
import { FlashList } from '@shopify/flash-list';
import { ArrowRight } from '@tamagui/lucide-icons';
import { TFunction } from 'i18next';
import { useEffect, useRef } from 'react';
import { Dimensions, Platform, Pressable } from 'react-native';

const RecommendedProductsNudgeComponent = ({
  products,
  t,
  sourcePage,
}: {
  products: IRecommendedProductNudgeDataModel[];
  t: TFunction;
  sourcePage: string;
}) => {
  const { width } = Dimensions.get('window');
  const cardWidth = width / 3 - 16; // Ensuring 3 cards fit with spacing
  const enqueue = useRef(useQueuedEventStore((state) => state.enqueue)).current;
  const { push } = useRouter();
  const userProfile = useProfileStore((state) => state?.userProfile);
  const deviceId = useProfileStore((state) => state?.deviceId);

  const renderItem = ({
    item,
    index,
  }: {
    item: IRecommendedProductNudgeDataModel;
    index: number;
  }) => (
    <Pressable testID="kisan_tId_ProductNudge" onPress={() => handleProductNudgeClick(item)} key={index}>
      <View
        width={cardWidth}
        paddingHorizontal={8}
        borderRadius={4}
        backgroundColor="#FFF5E1"
        margin={4}
        alignItems="center"
        height={140}
        justifyContent="space-between"
        paddingVertical={8} 
      >
        <View 
          width="100%" 
          height={sourcePage === 'home Page' ? 70 : 90}
          justifyContent="center" 
        >
          <Image
            source={{ uri: item.mediaDetails?.[0]?.mediaUrl }}
            width={96}
            height={ sourcePage  === 'home Page' ? 70: 90}
            resizeMode={'contain'}
          />
        </View>
        <XStack 
          width="100%"
          alignItems="center" 
          justifyContent="space-between"
          paddingHorizontal={4}
          minHeight={32}
        >
          <Text 
            fontSize={Platform.OS == 'web' ? 12 : 14} 
            fontWeight="500" 
            color="$darkGrey" 
            numberOfLines={2}
            flex={1}
            marginRight={4} 
          >
            {item?.content?.name}
          </Text>
          <ArrowRight size={18} color="#000" />
        </XStack>
      </View>
    </Pressable>
  );

  const handleProductNudgeClick = (item) => {
    push({
      pathname: `/edTech/product/productDetail/${item?.content?.productId}`,
      query: {
        productId: item?.content?.productId,
        productCategory: item?.content?.productCategory,
        productSubcategory: item?.content?.productSubcategory,
      },
    });
    handleProductNudgeClickEvent(item);
  };

  const handleProductNudgeClickEvent = (item) => {
    const productNudgeClickEvent: INinjaEvent = {
      name: KissanEventType.RECOMMENDED_PRODUCT_CLICK,
      screenDetails: { screen: 'feed video page' },
      eventParams: [
        addEventParam('product', item?.content?.name),
        addEventParam('productId', item?.content?.productId),
        addEventParam('productCategory', item?.content?.productCategory),
        addEventParam('productSubCategory', item?.content?.productSubcategory),
      ],
    };
    enqueue(productNudgeClickEvent);
  };

  const handleProductNudgeImpressionEvent = (item) => {
    const productNudgeImpressionEvent: INinjaEvent = {
      name: KissanEventType.PRODUCT_NUDGE_VIEWED_MORETHAN_ONE_SECOND,
      screenDetails: { screen: sourcePage },
      eventParams: [
        addEventParam('productCategory', item?.content?.productCategory),
      ],
    };
    enqueue(productNudgeImpressionEvent);
  };

  useEffect(() => {
    if (products.length > 0) {
      handleProductNudgeImpressionEvent(products[0]);
    }
  },[products]);

  return (
    <YStack>
      <Paragraph fontSize={17} fontWeight="500" marginBottom={8} color="$usertextColor">
        {t('page.recommendedProducts')}
      </Paragraph>
      <View flex={1}>
        <FlashList
          data={products}
          horizontal
          showsHorizontalScrollIndicator={false}
          estimatedItemSize={220}
          keyExtractor={(item, index) => index.toString()}
          renderItem={renderItem}
        />
      </View>
    </YStack>
  );
};

export { RecommendedProductsNudgeComponent };
